import { Link } from "react-router-dom";
import { Facebook, MapPin, Phone, Mail, ExternalLink } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { formatPhone, Logo } from "./Header";
import { track } from "../lib/api";

export const Footer = () => {
  const { settings, services, L } = useSite();
  return (
    <footer className="bg-[#0B1B3D] text-white relative grain" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <Logo light />
          <p className="text-slate-400 text-sm leading-relaxed mt-6">
            Professional residential and commercial construction, remodeling and improvement services.
          </p>
          <div className="flex gap-3 mt-6">
            {settings.facebook_url && (
              <a href={settings.facebook_url} target="_blank" rel="noreferrer" data-testid="footer-facebook"
                onClick={() => track("facebook_click")}
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-[#005AE0] hover:border-[#005AE0] transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
            )}
            {settings.nextdoor_url && (
              <a href={settings.nextdoor_url} target="_blank" rel="noreferrer" data-testid="footer-nextdoor"
                onClick={() => track("nextdoor_click")}
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-[#005AE0] hover:border-[#005AE0] transition-colors">
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
            {settings.google_business_url && (
              <a href={settings.google_business_url} target="_blank" rel="noreferrer" data-testid="footer-google"
                onClick={() => track("google_business_click")}
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-[#005AE0] hover:border-[#005AE0] transition-colors">
                <MapPin className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
        <div>
          <h4 className="font-heading font-bold uppercase tracking-widest text-sm mb-6 text-blue-300">{L.footer.navigation}</h4>
          <ul className="space-y-3 text-sm text-slate-300">
            {[[L.nav.home, "/"], [L.nav.about, "/about"], [L.nav.projects, "/projects"], [L.nav.ba, "/before-after"], [L.nav.reviews, "/reviews"], [L.nav.contact, "/contact"]].map(([l, to]) => (
              <li key={to}><Link to={to} className="hover:text-white transition-colors">{l}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-heading font-bold uppercase tracking-widest text-sm mb-6 text-blue-300">{L.footer.services}</h4>
          <ul className="space-y-3 text-sm text-slate-300">
            {services.slice(0, 7).map((s) => (
              <li key={s.id}><Link to={`/services/${s.slug}`} className="hover:text-white transition-colors">{s.title}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-heading font-bold uppercase tracking-widest text-sm mb-6 text-blue-300">{L.footer.contact}</h4>
          <ul className="space-y-4 text-sm text-slate-300">
            <li>
              <a href={`tel:${settings.phone}`} data-testid="footer-phone" onClick={() => track("phone_click", { location: "footer" })}
                className="flex items-center gap-3 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-[#005AE0]" /> {formatPhone(settings.phone)}
              </a>
            </li>
            <li>
              <a href={`mailto:${settings.email}`} data-testid="footer-email" onClick={() => track("email_click")}
                className="flex items-center gap-3 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-[#005AE0]" /> {settings.email}
              </a>
            </li>
            <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-[#005AE0]" /> {settings.service_area}</li>
          </ul>
          <Link to="/estimate" data-testid="footer-estimate-btn" className="btn-primary mt-8 !py-3 !px-6 !text-xs">
            {L.footer.request}
          </Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} Noyola Construction LLC. {L.footer.rights}</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-white transition-colors">{L.footer.privacy}</Link>
            <Link to="/terms" className="hover:text-white transition-colors">{L.footer.terms}</Link>
            {settings.yosi_url ? (
              <a href={settings.yosi_url} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">
                {L.footer.designed}
              </a>
            ) : (
              <span>{L.footer.designed}</span>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};
